import type { SdkPageContext } from '@ekhodealer/ekho-common';

/**
 * Hard cap on the total stripped-DOM-text payload. Keeps socket frames and
 * analytics payloads compact while retaining enough for semantic context.
 */
const MAX_CONTENT_LENGTH = 8000;

/** Per-element value cap. */
const MAX_ELEMENT_LENGTH = 160;

/** Tags whose subtrees produce no useful natural-language content. */
const EXCLUDED_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'IFRAME',
  'TEMPLATE',
  'SVG',
  'CANVAS',
  'NAV',
  'HEADER',
  'FOOTER',
  'OPTION',
]);

/** ARIA landmark roles that identify chrome regions on generic elements. */
const EXCLUDED_ROLES = new Set(['navigation', 'banner', 'contentinfo']);

/** CSS selector covering the Ekho SDK's own DOM — excluded from extraction. */
const WIDGET_SELECTOR =
  '#ekho-embedded-ai-assistant-root, [data-ekho-widget], #ekho-chat-widget, #ekho-shadow-host';

function emptyContext(): SdkPageContext {
  return { url: '', title: '', content: '' };
}

function pickExtractionRoot(): HTMLElement | null {
  const main = document.querySelector<HTMLElement>('main, [role="main"]');
  return main ?? document.body ?? null;
}

function isHidden(el: Element, hasLayout: boolean): boolean {
  if (el.getAttribute('aria-hidden') === 'true') return true;
  if (!(el instanceof HTMLElement)) return false;
  if (el.hidden) return true;
  const style = el.style;
  if (style.display === 'none' || style.visibility === 'hidden') return true;
  const computed = window.getComputedStyle?.(el);
  if (!computed) return false;
  if (computed.display === 'none' || computed.visibility === 'hidden') return true;
  if (computed.opacity === '0') return true;
  if (hasLayout && el.offsetWidth === 0 && el.offsetHeight === 0) return true;
  return false;
}

function normalize(value: string | null | undefined): string {
  if (!value) return '';
  const collapsed = value.replaceAll(/\s+/g, ' ').trim();
  return collapsed.length <= MAX_ELEMENT_LENGTH
    ? collapsed
    : `${collapsed.slice(0, MAX_ELEMENT_LENGTH)}…`;
}

function pushFragment(text: string, out: string[], state: { length: number }): void {
  state.length += (out.length > 0 ? 2 : 0) + text.length;
  out.push(text);
}

/** Returns true when an element should be skipped entirely during the walk. */
function shouldSkipElement(el: Element, hasLayout: boolean): boolean {
  if (EXCLUDED_TAGS.has(el.tagName)) return true;
  if (EXCLUDED_ROLES.has(el.getAttribute('role') ?? '')) return true;
  if (el instanceof HTMLElement && el.closest(WIDGET_SELECTOR)) return true;
  return isHidden(el, hasLayout);
}

/** Extract semantic text from an element (alt, aria-label) before recursing into children. */
function extractElementText(el: Element, out: string[], state: { length: number }): void {
  if (el instanceof HTMLImageElement) {
    const alt = normalize(el.alt);
    if (alt) pushFragment(alt, out, state);
  }
  const ariaLabel = normalize(el.getAttribute('aria-label'));
  if (ariaLabel) pushFragment(ariaLabel, out, state);
}

function walk(node: Node, out: string[], state: { length: number; hasLayout: boolean }): void {
  if (state.length >= MAX_CONTENT_LENGTH) return;

  if (node.nodeType === Node.TEXT_NODE) {
    const text = normalize(node.textContent);
    if (text) pushFragment(text, out, state);
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  if (shouldSkipElement(el, state.hasLayout)) return;

  extractElementText(el, out, state);

  for (const child of Array.from(el.childNodes)) {
    if (state.length >= MAX_CONTENT_LENGTH) return;
    walk(child, out, state);
  }
}

function truncateAtCommaBoundary(content: string): string {
  if (content.length <= MAX_CONTENT_LENGTH) return content;
  const slice = content.slice(0, MAX_CONTENT_LENGTH);
  const lastBoundary = slice.lastIndexOf(', ');
  const cutAt = lastBoundary > 0 ? lastBoundary : MAX_CONTENT_LENGTH;
  return `${content.slice(0, cutAt)}…`;
}

/**
 * Extract the buyer's current page context — URL, title, and a stripped
 * DOM-text blob. Capped at MAX_CONTENT_LENGTH; SSR-safe; widget-aware.
 */
export function extractPageContent(): SdkPageContext {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return emptyContext();
  }

  const url = window.location?.href ?? '';
  const title = document.title ?? '';

  const root = pickExtractionRoot();
  if (!root) return { url, title, content: '' };

  const fragments: string[] = [];
  const hasLayout = (document.body?.offsetWidth ?? 0) > 0 || (document.body?.offsetHeight ?? 0) > 0;
  const state = { length: 0, hasLayout };
  walk(root, fragments, state);

  const content = truncateAtCommaBoundary(fragments.join(', '));
  return { url, title, content };
}
